"use client";

import { c as _c } from "react/compiler-runtime";
import { useT, useGameLocale, type TFn } from "@/lib/i18n";
import React, { useState } from "react";
import { Link } from "@/i18n/navigation";
import { useBetaPrefix } from "@/lib/api/prefix.client";
const API_BASE = process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:8000";
import { imageUrl, fullCardUrl } from "@/lib/image-url";
import { splitRichLinks, type EntityNameLink, type LinkWord } from "@/lib/rich-links";
export interface RelatedCard {
  id: string;
  name: string;
  image_url: string | null;
  type: string;
  rarity: string;
  cost: number;
}
const COLOR_CLASSES: Record<string, string> = {
  gold: "text-[var(--accent-gold)]",
  red: "text-danger",
  blue: "text-info",
  green: "text-success",
  purple: "text-special",
  orange: "text-warning",
  pink: "text-special",
  aqua: "text-info"
};
const EFFECT_CLASSES: Record<string, string> = {
  sine: "rich-sine",
  jitter: "rich-jitter",
  b: "font-bold",
  i: "italic"
};
interface Token {
  type: "text" | "open" | "close" | "energy" | "star" | "placeholder";
  value: string;
  tag?: string;
  count?: number;
}

// Tags that should be silently stripped (opening and closing)
const STRIP_TAGS = new Set(["font_size", "thinky_dots", "rainbow"]);
function tokenize(text: string): Token[] {
  const tokens: Token[] = [];
  const regex = /\[(\/?)(\w+)(?:[=:]([^\]]*))?\]/g;
  let lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > lastIndex) {
      tokens.push({
        type: "text",
        value: text.slice(lastIndex, m.index)
      });
    }
    const isClose = m[1] === "/";
    const tag = m[2];
    const num = m[3];
    if (STRIP_TAGS.has(tag)) {
      // Silently strip these tags (both open and close)
    } else if (tag === "energy" && num) {
      tokens.push({
        type: "energy",
        value: m[0],
        count: num === "X" ? -1 : parseInt(num)
      });
    } else if (tag === "star" && num) {
      tokens.push({
        type: "star",
        value: m[0],
        count: num === "X" ? -1 : parseInt(num)
      });
    } else if (!isClose && !COLOR_CLASSES[tag] && !EFFECT_CLASSES[tag] && /^[A-Z]/.test(tag)) {
      // Dynamic placeholder like [Card], [Relic], [Potion]
      tokens.push({
        type: "placeholder",
        value: tag
      });
    } else if (isClose) {
      tokens.push({
        type: "close",
        value: m[0],
        tag
      });
    } else if (COLOR_CLASSES[tag] || EFFECT_CLASSES[tag]) {
      tokens.push({
        type: "open",
        value: m[0],
        tag
      });
    } else {
      // Unknown tag, pass through as text
      tokens.push({
        type: "text",
        value: m[0]
      });
    }
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < text.length) {
    tokens.push({
      type: "text",
      value: text.slice(lastIndex)
    });
  }
  return tokens;
}
interface StyledNode {
  text?: string;
  children?: StyledNode[];
  classes: string[];
  isEnergy?: boolean;
  isStar?: boolean;
  isPlaceholder?: boolean;
  count?: number;
}
function buildTree(tokens: Token[]): StyledNode {
  const root: StyledNode = {
    children: [],
    classes: []
  };
  const stack: StyledNode[] = [root];
  for (const token of tokens) {
    const current = stack[stack.length - 1];
    if (token.type === "text") {
      if (!current.children) current.children = [];
      current.children.push({
        text: token.value,
        classes: []
      });
    } else if (token.type === "open" && token.tag) {
      const cls = COLOR_CLASSES[token.tag] || EFFECT_CLASSES[token.tag] || "";
      const node: StyledNode = {
        children: [],
        classes: cls ? [cls] : []
      };
      if (!current.children) current.children = [];
      current.children.push(node);
      stack.push(node);
    } else if (token.type === "close") {
      if (stack.length > 1) stack.pop();
    } else if (token.type === "energy") {
      if (!current.children) current.children = [];
      current.children.push({
        isEnergy: true,
        count: token.count,
        classes: []
      });
    } else if (token.type === "star") {
      if (!current.children) current.children = [];
      current.children.push({
        isStar: true,
        count: token.count,
        classes: []
      });
    } else if (token.type === "placeholder") {
      if (!current.children) current.children = [];
      current.children.push({
        isPlaceholder: true,
        text: token.value,
        classes: []
      });
    }
  }
  return root;
}
let keyCounter = 0;
function renderNode(node: StyledNode, energyIcon: string, t: TFn, relatedCards?: RelatedCard[]): React.ReactNode {
  const key = keyCounter++;
  if (node.isEnergy) {
    if (node.count === -1) {
      return <span key={key}>
          <img src={imageUrl(`/static/images/icons/${energyIcon}_energy_icon.webp`)} alt={t("energy")} className="inline-block w-4 h-4 align-text-bottom" crossOrigin="anonymous" />
          X
        </span>;
    }
    const icons = [];
    for (let i = 0; i < (node.count ?? 1); i++) {
      icons.push(<img key={i} src={imageUrl(`/static/images/icons/${energyIcon}_energy_icon.webp`)} alt={t("energy")} className="inline-block w-4 h-4 align-text-bottom" crossOrigin="anonymous" />);
    }
    return <span key={key}>{icons}</span>;
  }
  if (node.isStar) {
    const icons = [];
    for (let i = 0; i < (node.count ?? 1); i++) {
      icons.push(<img key={i} src={imageUrl("/static/images/icons/star_icon.webp")} alt={t("star")} className="inline-block w-4 h-4 align-text-bottom" crossOrigin="anonymous" />);
    }
    return <span key={key}>{icons}</span>;
  }
  if (node.isPlaceholder) {
    return <span key={key} className="text-[var(--text-muted)] italic">
        {node.text}
      </span>;
  }
  if (node.text !== undefined) {
    if (relatedCards?.length) {
      const segments = splitWithCardRefs(node.text, relatedCards);
      if (segments.some(s => s.card)) {
        return <React.Fragment key={key}>
            {segments.map((seg, i) => seg.card ? <CardHoverTip key={i} card={seg.card} isUpgraded={seg.isUpgraded}>
                  {seg.text}
                </CardHoverTip> : <React.Fragment key={i}>{seg.text}</React.Fragment>)}
          </React.Fragment>;
      }
    }
    return node.text;
  }
  const children = (node.children ?? []).map(child => renderNode(child, energyIcon, t, relatedCards));
  if (node.classes.length === 0) {
    return <React.Fragment key={key}>{children}</React.Fragment>;
  }
  return <span key={key} className={node.classes.join(" ")}>
      {children}
    </span>;
}

/**
 * Renders a description string containing color tags, effect tags, icons, and placeholders
 * into styled React nodes.
 *
 * Supported tags:
 * - Colors: [gold], [red], [blue], [green], [purple], [orange], [pink], [aqua]
 * - Effects: [sine] (wavy), [jitter] (shake), [b] (bold)
 * - Icons: [energy:N], [star:N]
 * - Placeholders: [Card], [Relic], [Potion] (runtime-dynamic)
 */
// Valid rich text tags that should NOT be cleaned
const VALID_TAGS = new Set(["gold", "red", "blue", "green", "purple", "orange", "pink", "aqua", "sine", "jitter", "b", "i", "/gold", "/red", "/blue", "/green", "/purple", "/orange", "/pink", "/aqua", "/sine", "/jitter", "/b", "/i"]);

/**
 * Clean SmartFormat template variables and dynamic bracket vars
 * that can't be resolved statically.
 */
function cleanTemplateVars(text: string): string {
  // The literal {} appears inside SmartFormat plural branches as the var's
  // value placeholder (e.g. {Repeat:plural:| [blue]{}[/blue] times}). Resolve
  // it to the same blue "X" we use for runtime-dynamic numeric vars before the
  // plural regex runs, otherwise its [^}]* plural capture stops at the inner
  // closing brace and leaks the trailing literal as "{ times}" on the page.
  text = text.replace(/\{\}/g, "[blue]X[/blue]");
  // Handle {Var:plural:singular|plural} → plural
  text = text.replace(/\{(\w+):plural:([^|}]*)\|([^}]*)\}/g, (_m, _v, _s, p) => p);
  // Handle {IsMultiplayer:A|B} → B (second option)
  text = text.replace(/\{IsMultiplayer:([^|}]*)\|([^}]*)\}/g, (_m, _a, b) => b);
  // Handle {Repeat:plural:|...} → ""
  text = text.replace(/\{Repeat:plural:\|[^}]*\}/g, "");
  // Handle remaining {VarName} → styled "X" (runtime-dynamic value)
  text = text.replace(/\{\w+\}/g, "[blue]X[/blue]");
  // Handle dynamic [Var] square bracket vars, but preserve valid rich text tags and icons
  text = text.replace(/\[([^\]]+)\]/g, (match, inner) => {
    // Preserve valid tags, icon tags, and parameterized tags like font_size=22
    if (VALID_TAGS.has(inner) || /^(energy|star):(\d+|X)$/.test(inner)) return match;
    if (/^\/?(font_size|thinky_dots|rainbow)(=\d+)?$/.test(inner)) return match;
    // Numeric vars → styled "X" (runtime-dynamic value)
    if (/^(Amount|Passive|Evoke|Damage Decrease|Damage Increase|EntrantNumber|CardCount)$/i.test(inner)) return "[blue]X[/blue]";
    // Context-dependent names → strip
    if (/^(Owner Name|OwnerName|On Player|Applier|Covering|Is Multiplayer)$/i.test(inner)) return "";
    // Dotted property access like [Applier Name.String Value] → strip
    if (inner.includes(".")) return "";
    // Any other capitalized bracket content → strip
    if (/^[A-Z]/.test(inner)) return "";
    return match;
  });
  // Clean up extra spaces
  text = text.replace(/  +/g, " ").trim();
  return text;
}
function CardHoverTip(t0) {
  const $ = _c(19);
  const {
    card,
    isUpgraded,
    children
  } = t0;
  const t = useT();
  const [show, setShow] = useState(false);
  const [failed, setFailed] = useState(false);
  const lang = useGameLocale();
  const displayName = isUpgraded ? `${card.name}+` : card.name;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => setShow(true);
    t2 = () => setShow(false);
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  if ($[2] !== card.id) {
    t3 = card.id.toLowerCase();
    $[2] = card.id;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = `/cards/${t3}`;
  let t5;
  if ($[4] !== children || $[5] !== t4) {
    t5 = <Link href={t4} className="underline decoration-dotted underline-offset-2 decoration-[var(--text-muted)] hover:decoration-[var(--accent-gold)] transition-colors">{children}</Link>;
    $[4] = children;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== card.id || $[8] !== card.image_url || $[9] !== displayName || $[10] !== failed || $[11] !== isUpgraded || $[12] !== lang || $[13] !== show || $[14] !== t) {
    t6 = show && <span className="pointer-events-none absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 w-40"><img src={failed && card.image_url ? imageUrl(card.image_url) : fullCardUrl(card.id.toLowerCase(), !!isUpgraded, "stable", lang)} alt={t("{name} - Slay the Spire 2 Card", {
        name: displayName
      })} className="w-40 h-auto drop-shadow-[0_8px_24px_rgba(0,0,0,0.7)]" crossOrigin="anonymous" loading="lazy" onError={() => setFailed(true)} /></span>;
    $[7] = card.id;
    $[8] = card.image_url;
    $[9] = displayName;
    $[10] = failed;
    $[11] = isUpgraded;
    $[12] = lang;
    $[13] = show;
    $[14] = t;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== t5 || $[17] !== t6) {
    t7 = <span className="relative inline" onMouseEnter={t1} onMouseLeave={t2}>{t5}{t6}</span>;
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  return t7;
}

/** Split a text string around related card name matches, returning segments. */
function splitWithCardRefs(text: string, cards: RelatedCard[]): {
  text: string;
  card?: RelatedCard;
  isUpgraded?: boolean;
}[] {
  if (!cards.length) return [{
    text
  }];

  // Build patterns sorted by name length desc (longer names match first)
  const sorted = [...cards].sort((a, b) => b.name.length - a.name.length);
  const patterns = sorted.flatMap(c => {
    // Match exact name, common plural forms, and upgraded suffix (+)
    const escaped = c.name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    return [{
      re: new RegExp(`(${escaped}(?:s|es)?(\\+)?)`, "gi"),
      card: c
    }];
  });
  let segments: {
    text: string;
    card?: RelatedCard;
    isUpgraded?: boolean;
  }[] = [{
    text
  }];
  for (const {
    re,
    card
  } of patterns) {
    const next: {
      text: string;
      card?: RelatedCard;
      isUpgraded?: boolean;
    }[] = [];
    for (const seg of segments) {
      if (seg.card) {
        next.push(seg);
        continue;
      }
      let last = 0;
      let m: RegExpExecArray | null;
      re.lastIndex = 0;
      while ((m = re.exec(seg.text)) !== null) {
        if (m.index > last) {
          next.push({
            text: seg.text.slice(last, m.index)
          });
        }
        next.push({
          text: m[1],
          card,
          isUpgraded: !!m[2]
        });
        last = m.index + m[0].length;
      }
      if (last < seg.text.length) {
        next.push({
          text: seg.text.slice(last)
        });
      }
    }
    segments = next;
  }
  return segments;
}
export type InteractiveWord = LinkWord;
function WordTooltip(t0) {
  const $ = _c(12);
  const {
    word,
    info,
    children
  } = t0;
  const [show, setShow] = useState(false);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => setShow(true);
    t2 = () => setShow(false);
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  if ($[2] !== children || $[3] !== info.href) {
    t3 = <Link href={info.href} className="underline decoration-dotted underline-offset-2 decoration-[var(--text-muted)] hover:decoration-[var(--accent-gold)] transition-colors">{children}</Link>;
    $[2] = children;
    $[3] = info.href;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== info.tooltip || $[6] !== show || $[7] !== word) {
    t4 = show && info.tooltip && <span className="absolute z-[100] bottom-full left-0 mb-2 w-52 p-2.5 rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-card)] shadow-xl pointer-events-none"><span className="font-semibold text-xs text-[var(--text-primary)] block">{word}</span><span className="text-[10px] text-[var(--text-secondary)] leading-relaxed block mt-0.5"><RichDescriptionSimple text={info.tooltip} /></span></span>;
    $[5] = info.tooltip;
    $[6] = show;
    $[7] = word;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t3 || $[10] !== t4) {
    t5 = <span className="relative inline" onMouseEnter={t1} onMouseLeave={t2}>{t3}{t4}</span>;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}

/** Simple renderer without interactive words (for tooltips to avoid infinite recursion) */
export function RichDescriptionSimple({
  text
}: {
  text: string;
}) {
  const t = useT();
  keyCounter = 0;
  const cleaned = cleanTemplateVars(text);
  const tokens = tokenize(cleaned);
  const tree = buildTree(tokens);
  return <>{renderNode(tree, "colorless", t)}</>;
}
export default function RichDescription({
  text,
  energyIcon = "colorless",
  relatedCards,
  interactiveWords,
  entityLinks,
  nestedInLink
}: {
  text: string;
  energyIcon?: string;
  relatedCards?: RelatedCard[];
  interactiveWords?: Record<string, InteractiveWord>;
  /** Catalog entity names ("Lantern") to link to their pages. */
  entityLinks?: EntityNameLink[];
  /** Set when the rendered text already sits inside an anchor: never nest links. */
  nestedInLink?: boolean;
}) {
  const t = useT();
  const bp = useBetaPrefix();
  // One link per distinct entity for the whole description.
  const linkedEntityHrefs = new Set<string>();
  keyCounter = 0;
  const cleaned = cleanTemplateVars(text);
  const tokens = tokenize(cleaned);
  const tree = buildTree(tokens);
  function renderWithInteractive(node: StyledNode): React.ReactNode {
    const key = keyCounter++;
    if (node.isEnergy || node.isStar || node.isPlaceholder) {
      return renderNode(node, energyIcon, t, relatedCards);
    }
    if (node.text !== undefined) {
      // Check card refs first, card names take priority over interactive words
      // (e.g. "Minion Strikes" should link to the card, not match "Minion" power)
      if (relatedCards?.length) {
        const cardSegs = splitWithCardRefs(node.text, relatedCards);
        if (cardSegs.some(s => s.card)) {
          return <React.Fragment key={key}>
              {cardSegs.map((seg, i) => seg.card ? <CardHoverTip key={i} card={seg.card} isUpgraded={seg.isUpgraded}>
                    {seg.text}
                  </CardHoverTip> : <React.Fragment key={i}>{seg.text}</React.Fragment>)}
            </React.Fragment>;
        }
      }
      // Keyword and entity mentions: keyword links keep their tooltip,
      // entity names link as plain text. splitRichLinks only matches what
      // the card-ref pass left plain, so links never nest.
      const segments = splitRichLinks(node.text, {
        words: interactiveWords,
        links: nestedInLink ? undefined : entityLinks,
        linked: linkedEntityHrefs
      });
      if (segments.some(s_0 => s_0.info || s_0.link)) {
        return <React.Fragment key={key}>
            {segments.map((seg_0, i_0) => {
            if (seg_0.info) {
              return <WordTooltip key={i_0} word={seg_0.word!} info={seg_0.info}>
                    {seg_0.text}
                  </WordTooltip>;
            }
            if (seg_0.link) {
              return <Link key={i_0} href={`${bp}/${seg_0.link.href.replace(/^\/+/, "")}`} className="hover:underline underline-offset-2">
                    {seg_0.text}
                  </Link>;
            }
            return <React.Fragment key={i_0}>{seg_0.text}</React.Fragment>;
          })}
          </React.Fragment>;
      }
      return node.text;
    }
    const children = (node.children ?? []).map(child => renderWithInteractive(child));
    if (node.classes.length === 0) return <React.Fragment key={key}>{children}</React.Fragment>;
    return <span key={key} className={node.classes.join(" ")}>
        {children}
      </span>;
  }
  const hasWords = interactiveWords && Object.keys(interactiveWords).length > 0;
  if (hasWords || entityLinks?.length && !nestedInLink) {
    return <>{renderWithInteractive(tree)}</>;
  }
  return <>{renderNode(tree, energyIcon, t, relatedCards)}</>;
}